import { useState, useEffect, useRef } from 'react';
import { Modal, Stack, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { PsychomotorDevelopmentInputItem } from '@/components/inputs/PsychomotorDevelopmentInputItem';
import {
  type PsychomotorDevelopmentItem,
  type PsychomotorDevelopmentType,
  PSYCHOMOTOR_DEVELOPMENT_TYPES as ALL_TYPES,
} from '@/types/psychomotor-development.types';

export interface PsychomotorDevelopmentInputModalProps {
  opened: boolean;
  onClose: () => void;
  /** Callback to save assessment. Returns the saved items. */
  onSave: (items: PsychomotorDevelopmentItem[]) => Promise<void>;
  /** Callback called after successful save. */
  onSaveSuccess?: (items: PsychomotorDevelopmentItem[]) => Promise<void>;
  items?: PsychomotorDevelopmentItem[];
  mode: 'add' | 'edit';
}

const createEmptyItem = (
  type: PsychomotorDevelopmentType
): PsychomotorDevelopmentItem => ({
  type,
  value: null,
  observations: '',
});

const createEmptyItems = (): PsychomotorDevelopmentItem[] => {
  return ALL_TYPES.map((type) => createEmptyItem(type));
};

export const PsychomotorDevelopmentInputModal = ({
  opened,
  onClose,
  onSave,
  onSaveSuccess,
  items,
  mode,
}: PsychomotorDevelopmentInputModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Form state
  const [formItems, setFormItems] =
    useState<PsychomotorDevelopmentItem[]>(createEmptyItems());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<PsychomotorDevelopmentItem[] | null>(null);

  // Reset form when modal opens or items change
  useEffect(() => {
    if (opened) {
      setInputKey((prev) => prev + 1);
      const initialItems =
        items && items.length > 0 ? [...items] : createEmptyItems();
      setFormItems(initialItems);
      // Capture baseline immediately after setting state
      baselineRef.current = [...initialItems];
    }
  }, [opened, items]);

  // Update item
  const updateItem = (
    type: PsychomotorDevelopmentType,
    field: 'value' | 'observations',
    value: boolean | null | string
  ) => {
    const updated = formItems.map((item) =>
      item.type === type ? { ...item, [field]: value } : item
    );
    setFormItems(updated);
  };

  const getItem = (
    type: PsychomotorDevelopmentType
  ): PsychomotorDevelopmentItem => {
    return (
      formItems.find((item) => item.type === type) || createEmptyItem(type)
    );
  };

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      return !isEqual(formItems, baselineRef.current);
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormItems([...baselineRef.current]);
    }
    setInputKey((prev) => prev + 1);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(formItems);
      // Notify parent component of successful save and wait for reload
      if (onSaveSuccess) {
        await onSaveSuccess(formItems);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save psychomotor development:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to save psychomotor development'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={
        mode === 'add'
          ? t('psychomotorDevelopment.addAssessment', {
              ns: 'patient-pages',
              defaultValue: 'Add Psychomotor Development Assessment',
            })
          : t('psychomotorDevelopment.editAssessment', {
              ns: 'patient-pages',
              defaultValue: 'Edit Psychomotor Development Assessment',
            })
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Error Alert */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        {/* Form inputs */}
        <Stack gap="lg" key={inputKey}>
          {ALL_TYPES.map((type) => {
            const item = getItem(type);
            return (
              <PsychomotorDevelopmentInputItem
                key={type}
                item={item}
                type={type}
                onValueChange={(value) => updateItem(type, 'value', value)}
                onObservationsChange={(observations) =>
                  updateItem(type, 'observations', observations)
                }
              />
            );
          })}
        </Stack>

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={
            mode === 'add'
              ? t('psychomotorDevelopment.addButton', {
                  ns: 'patient-pages',
                  defaultValue: 'Add',
                })
              : t('psychomotorDevelopment.saveButton', {
                  ns: 'patient-pages',
                  defaultValue: 'Save',
                })
          }
        />
      </Stack>
    </Modal>
  );
};
